import { useEffect, useRef, useState } from 'react'

const HIDE_THRESHOLD_PX = 40
const SHOW_THRESHOLD_PX = 10
const TOP_GUARD_PX = 80
const SETTLING_DISTANCE_PX = 150

/**
 * Returns whether the header should be visible based on scroll direction.
 *
 * Accumulates scroll distance in the current direction, resetting the accumulator
 * when direction reverses. Hides the header after 40px of sustained downward
 * scroll and shows it after 10px of upward scroll. Always shows within 80px
 * of the top of the scroll container.
 *
 * @param scrollContainerRef Optional ref to the scrolling element. If omitted,
 * window scroll is used.
 * @param headerRef Optional ref to the header element. When provided, the
 * hide/show behavior only activates once the element has reached the top of the
 * viewport AND the user has scrolled an additional settling distance past that
 * point. Prevents premature hiding when content above (e.g., a shoppable video)
 * pushes the header below the fold and the user still has downward momentum.
 */
export function useHeaderScrollVisibility(
    scrollContainerRef?: React.RefObject<HTMLElement | null>,
    headerRef?: React.RefObject<HTMLElement | null>,
): boolean {
    const [isVisible, setIsVisible] = useState(true)

    const lastScrollTopRef = useRef(0)
    const accumulatedRef = useRef(0)
    const directionRef = useRef<'up' | 'down' | null>(null)
    const hasReachedTopRef = useRef(false)
    const settlingDistanceRef = useRef(0)
    const headerOffsetRef = useRef(0)

    useEffect(() => {
        const element = scrollContainerRef?.current
        const target: HTMLElement | Window = element ?? window
        const getScrollTop = (): number =>
            element ? element.scrollTop : window.scrollY

        const getDocumentOffset = (el: HTMLElement): number => {
            let offset = 0
            let current: Element | null = el
            while (current instanceof HTMLElement) {
                offset += current.offsetTop
                current = current.offsetParent
            }
            return offset
        }

        const updateHeaderOffset = () => {
            if (!headerRef?.current) return
            headerOffsetRef.current = getDocumentOffset(headerRef.current)
        }

        updateHeaderOffset()
        lastScrollTopRef.current = getScrollTop()

        const handleScroll = () => {
            const currentScrollTop = getScrollTop()
            const delta = currentScrollTop - lastScrollTopRef.current
            lastScrollTopRef.current = currentScrollTop

            if (headerRef?.current) {
                const headerIsAtTop =
                    currentScrollTop >= headerOffsetRef.current
                if (!headerIsAtTop) {
                    hasReachedTopRef.current = false
                    settlingDistanceRef.current = 0
                    accumulatedRef.current = 0
                    directionRef.current = null
                    setIsVisible(true)
                    return
                }
                if (!hasReachedTopRef.current) {
                    hasReachedTopRef.current = true
                    settlingDistanceRef.current = 0
                }
                if (settlingDistanceRef.current < SETTLING_DISTANCE_PX) {
                    settlingDistanceRef.current += Math.abs(delta)
                    return
                }
            }

            if (currentScrollTop <= TOP_GUARD_PX) {
                accumulatedRef.current = 0
                directionRef.current = null
                hasReachedTopRef.current = false
                settlingDistanceRef.current = 0
                setIsVisible(true)
                return
            }

            if (delta === 0) return

            const nextDirection: 'up' | 'down' = delta > 0 ? 'down' : 'up'

            if (directionRef.current !== nextDirection) {
                directionRef.current = nextDirection
                accumulatedRef.current = 0
            }

            accumulatedRef.current += Math.abs(delta)

            if (
                nextDirection === 'down' &&
                accumulatedRef.current >= HIDE_THRESHOLD_PX
            ) {
                setIsVisible(false)
            } else if (
                nextDirection === 'up' &&
                accumulatedRef.current >= SHOW_THRESHOLD_PX
            ) {
                setIsVisible(true)
            }
        }

        target.addEventListener('scroll', handleScroll, { passive: true })
        window.addEventListener('resize', updateHeaderOffset, { passive: true })
        return () => {
            target.removeEventListener('scroll', handleScroll)
            window.removeEventListener('resize', updateHeaderOffset)
        }
    }, [scrollContainerRef, headerRef])

    return isVisible
}
